import React, { useEffect } from "react";
import moment from "moment";
import { FormattedMessage, useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import { withRouter } from "react-router-dom";
import queryString from "query-string";
import { toast } from "react-toastify";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChalkboardTeacher, faSync } from "@fortawesome/free-solid-svg-icons";
import { Blockquote, InlineLoading } from "@wfp/ui";

import Empty from "components/Empty";
import ModalExtended from "components/ModalExtended";
import { RepeaterItem } from "components/Repeater";
import ItemSwitcher from "components/ItemSwitcher";
import SyncError from "components/SyncSelect/SyncError";

import {
  getUnsyncedStores,
  getIsStoresFetching,
  getAllAttendanceStoresByDay,
  asyncSyncStart,
  asyncSyncReset,
  getAsyncSyncStatus,
  getAsyncSyncError,
  getIsAsyncSyncInProgress,
  getIsPendingSyncsChecked,
  checkPendingSyncs,
  getAsyncSyncProgress,
} from "data-handler/ducks/stores";

import SyncFooter from "./SyncFooter";

import {
  isMealAttendance,
  isStudentAttendance,
  isTakeHomeRationAttendance,
} from "helpers/stores";

import { getCurrentSchoolProfile } from "data-handler/ducks/schools";

import { getLoggedInOffline } from "data-handler/ducks/auth";
import { getIsWFPUser } from "data-handler/ducks/auth";
import { toastFormattedMessages } from "SCConstants";

import "./_syncSelect.scss";

const SyncSelect = ({ history, match }) => {
  const dispatch = useDispatch();
  const isFetching = useSelector(getIsStoresFetching);
  const unsyncedStores = useSelector(getUnsyncedStores);
  const isOffline = useSelector(getLoggedInOffline);
  const currentSchoolProfile = useSelector(getCurrentSchoolProfile);
  const attendanceStoresByDay = useSelector(getAllAttendanceStoresByDay);
  const asyncSyncStatus = useSelector(getAsyncSyncStatus);
  const asyncSyncError = useSelector(getAsyncSyncError);
  const asyncSyncProgress = useSelector(getAsyncSyncProgress);
  const isAsyncSyncInProgress = useSelector(getIsAsyncSyncInProgress);
  const isPendingSyncsChecked = useSelector(getIsPendingSyncsChecked);

  const isWFP = useSelector(getIsWFPUser);
  const intl = useIntl();

  const currentSchoolEnabledStudentAttendance =
    currentSchoolProfile.enable_student_attendance;

  const currentSchoolEnabledTHR = currentSchoolProfile.enable_take_home_rations;

  const urlParams = queryString.parse(history.location.search);
  const isOpen = urlParams.select === "sync";
  const schoolId = match.params.schoolId;

  useEffect(() => {
    // Only check for pending syncs if we're not already tracking an active sync
    if (isOpen && !isOffline && schoolId && !isAsyncSyncInProgress) {
      dispatch(checkPendingSyncs(schoolId));
    }
  }, [isOpen, isOffline, schoolId, dispatch, isAsyncSyncInProgress]);

  if (!isOpen) {
    return null;
  }

  const closeWindow = () => {
    // Allow the sync to continue in background
    // Only reset if sync is not in progress
    if (!isAsyncSyncInProgress) {
      dispatch(asyncSyncReset());
    } else {
      toast.warning(
        toastFormattedMessages.find(
          (e) => e.name === "toast.syncRunningInBackground"
        )?.label,
        {
          toastId: "syncRunningInBackground",
          position: "top-center",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: true,
          draggable: true,
        }
      );
    }
    history.push(`${history.location.pathname}?nav=true`);
  };

  const handleBackgroundSync = () => {
    if (asyncSyncStatus === "error") {
      dispatch(asyncSyncReset());
    }
    dispatch(asyncSyncStart(unsyncedStores, match.params.schoolId));
  };

  const hasUnsuccessfulSyncs =
    unsyncedStores.filter((datum) => {
      return Object.keys(datum).includes("error");
    }).length !== 0;

  const hasPreviousMonthsSyncs =
    unsyncedStores.find((item) => {
      if ("occurred_on" in item) {
        return moment(item.occurred_on).isBefore(
          moment().format("YYYY-MM-DD"),
          "month"
        );
      } else if ("occurred_at" in item) {
        return moment(item.occurred_at).isBefore(
          moment().format("YYYY-MM-DD"),
          "month"
        );
      } else if ("starts_on" in item) {
        return moment(item.starts_on).isBefore(
          moment().format("YYYY-MM-DD"),
          "month"
        );
      }
      return undefined;
    }) !== undefined;

  const isButtonDisabled =
    !isPendingSyncsChecked || isOffline || isWFP || unsyncedStores.length === 0;

  const renderCustomFooter = () => (
    <SyncFooter
      onBackgroundSync={handleBackgroundSync}
      onClose={closeWindow}
      isBackgroundSyncInProgress={isAsyncSyncInProgress}
      isDisabled={isButtonDisabled}
      asyncSyncStatus={asyncSyncStatus}
      progress={asyncSyncProgress}
    />
  );

  return (
    <ModalExtended
      open
      onRequestClose={closeWindow}
      modalLabel={
        <FormattedMessage
          id="SyncSelect.sync_data"
          defaultMessage="Sync data"
        />
      }
      modalHeading={
        <FormattedMessage
          id="SyncSelect.outstandingSyncs"
          defaultMessage="{unsyncedStoresLength} outstanding syncs"
          values={{ unsyncedStoresLength: unsyncedStores.length }}
        />
      }
      wide
      modalFooter={renderCustomFooter}
      primaryButtonDisabled={true}
      selectorPrimaryFocus={false}
    >
      <div>
        {isOffline && (
          <Blockquote kind="warning">
            <FormattedMessage
              id="SyncSelect.youAreOffline"
              defaultMessage="You're currently in offline mode. If you want to sync, you must login online."
            />
          </Blockquote>
        )}
        {hasUnsuccessfulSyncs && (
          <Blockquote kind="warning">
            <FormattedMessage
              id="SyncSelect.hasUnsuccessfulSyncs"
              defaultMessage="Data will not be updated until sync is successful."
            />
          </Blockquote>
        )}
        {asyncSyncStatus === "error" && asyncSyncError && (
          <Blockquote kind="error">
            <FormattedMessage
              id="SyncSelect.asyncError"
              defaultMessage="Sync failed: {error}. Please try again."
              values={{ error: asyncSyncError }}
            />
          </Blockquote>
        )}
        {asyncSyncStatus === "completed" && (
          <Blockquote kind="success">
            <FormattedMessage
              id="SyncSelect.syncCompleted"
              defaultMessage="Sync completed successfully!"
            />
          </Blockquote>
        )}
        {isAsyncSyncInProgress && (
          <Blockquote kind={asyncSyncProgress?.is_stale ? "warning" : "info"}>
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                width: "100%",
              }}
            >
              <span>
                {asyncSyncProgress?.is_stale ? (
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "0.5rem",
                    }}
                  >
                    <FontAwesomeIcon icon={faSync} spin />
                    <FormattedMessage
                      id="SyncSelect.reconnecting"
                      defaultMessage="Reconnecting..."
                    />
                  </span>
                ) : asyncSyncProgress ? (
                  <FormattedMessage
                    id="SyncSelect.syncInProgressWithStatus"
                    defaultMessage="Sync is in progress. Processing {current} of {total}..."
                    values={{
                      current: asyncSyncProgress.current,
                      total: asyncSyncProgress.total,
                    }}
                  />
                ) : (
                  <FormattedMessage
                    id="SyncSelect.syncInProgress"
                    defaultMessage="Sync is in progress. Please wait..."
                  />
                )}
              </span>
              {asyncSyncProgress?.updated_at &&
                typeof asyncSyncProgress.updated_at === "number" && (
                  <span style={{ color: "#888", fontSize: "0.85em" }}>
                    {moment.unix(asyncSyncProgress.updated_at).fromNow()}
                  </span>
                )}
            </div>
          </Blockquote>
        )}

        {/* Nothing to sync! */}
        {unsyncedStores.length === 0 && (
          <Empty
            title={intl.formatMessage({
              id: "SyncSelect.everything_synced",
              defaultMessage: "Everything synced",
            })}
            icon={<FontAwesomeIcon icon={faChalkboardTeacher} size="1x" />}
          >
            <FormattedMessage
              id="SyncSelect.update_something"
              defaultMessage="Update something"
            />
          </Empty>
        )}
        {/* Previous months syncs */}
        {hasPreviousMonthsSyncs && (
          <Blockquote kind="warning">
            <FormattedMessage
              id="SyncSelect.hasPreviousMonthsSync"
              defaultMessage="You are about to sync data for old months which could cause some changes for the next months"
            />
          </Blockquote>
        )}

        {/* Something to sync */}
        {unsyncedStores.map((store) => {
          let additional = null;
          const showItemLoader =
            (isFetching || isAsyncSyncInProgress) &&
            asyncSyncStatus !== "completed";
          if (showItemLoader) {
            additional = <InlineLoading />;
          }

          if (
            isMealAttendance(store) ||
            isStudentAttendance(store) ||
            isTakeHomeRationAttendance(store)
          ) {
            const data = attendanceStoresByDay[store.occurred_on];
            return (
              <RepeaterItem key={store.client_id} className="sync-select">
                <ItemSwitcher
                  store={data.mealAttendance}
                  studentStore={data.studentAttendance}
                  thrStore={data.thrAttendance}
                  storeToChooseType={store}
                  currentSchoolEnabledStudentAttendance={
                    currentSchoolEnabledStudentAttendance
                  }
                  currentSchoolEnabledTHR={currentSchoolEnabledTHR}
                  key={store.client_id}
                  additionalRight={additional}
                />
                <SyncError syncable={store} />
              </RepeaterItem>
            );
          } else {
            return (
              <RepeaterItem key={store.client_id} className="sync-select">
                <ItemSwitcher store={store} additionalRight={additional} />
                <SyncError syncable={store} />
              </RepeaterItem>
            );
          }
        })}
      </div>
    </ModalExtended>
  );
};

export default withRouter(SyncSelect);
